import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { SiteTitleProvider } from './contexts/SiteTitleContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ArticleContextMenuProvider } from './components/ArticleContextMenu';
import { ToastProvider } from './components/Toast';
import HomepageEditModal from './components/HomepageEditModal';
import CookieBanner from './components/CookieBanner';
import SEO from './components/SEO';
import NotFoundPage from './components/NotFoundPage';
import './App.css';

// Core pages imported directly to eliminate suspense/loader flashing
import RwandaNewsLayout from './components/RwandaNewsLayout';
import ArticlePage from './components/ArticlePage';

// Code splitting: Lazy load secondary components
const CategoryPage = lazy(() => import('./components/CategoryPage'));
const AdminPage = lazy(() => import('./components/AdminPage'));
const VideosPageWrapper = lazy(() => import('./components/VideosPageWrapper'));
const FullscreenArticleEditor = lazy(() => import('./components/FullscreenArticleEditor'));
const ReadingList = lazy(() => import('./components/ReadingList'));
const AuthorPage = lazy(() => import('./components/AuthorPage'));

// Legal & Info pages (lazy loaded)
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const DisclaimerPage = lazy(() => import('./pages/DisclaimerPage'));
const HelpPage = lazy(() => import('./pages/HelpPage'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      <p className="text-gray-500 dark:text-gray-400 text-sm">Loading...</p>
    </div>
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <LanguageProvider>
            <SiteTitleProvider>
              <ToastProvider>
                <ArticleContextMenuProvider>
                  <Router>
                  {/* Default SEO tags for the site */}
                  <SEO />
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/" element={<RwandaNewsLayout />} />
                      <Route path="/admin" element={<AdminPage />} />
                      <Route path="/write" element={<FullscreenArticleEditor />} />
                      <Route path="/category/:slug" element={<CategoryPage />} />
                      <Route path="/videos" element={<VideosPageWrapper />} />
                      <Route path="/library" element={<ReadingList />} />
                      <Route path="/saved" element={<ReadingList />} />
                      {/* Article Page for SEO - dedicated URLs */}
                      <Route path="/news/:slug" element={<ArticlePage />} />
                      {/* Author Page */}
                      <Route path="/author/:authorId" element={<AuthorPage />} />
                      {/* Legal & Info Pages */}
                      <Route path="/about" element={<AboutPage />} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/privacy" element={<PrivacyPage />} />
                      <Route path="/terms" element={<TermsPage />} />
                      <Route path="/disclaimer" element={<DisclaimerPage />} />
                      <Route path="/help" element={<HelpPage />} />
                      {/* 404 - Catch all unmatched routes */}
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </Suspense>
                  {/* Cookie Consent Banner */}
                  <CookieBanner />
                </Router>
                {/* Edit modal that can open on any page */}
                <HomepageEditModal />
              </ArticleContextMenuProvider>
            </ToastProvider>
          </SiteTitleProvider>
        </LanguageProvider>
      </AuthProvider>
    </ThemeProvider>
  </HelmetProvider>
  );
}

export default App;
